import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Filter, Loader2, LayoutGrid, Rows3, X } from "lucide-react";
import { api } from "@/lib/api";
import type { Property, PropertyQuery } from "@/types/property";
import { PageBanner } from "@/components/site/PageBanner";
import { PropertyCard } from "@/components/site/PropertyCard";
import { PropertyTable } from "@/components/site/PropertyTable";
import { SearchBar } from "@/components/site/SearchBar";
import { SectionHeading } from "@/components/site/SectionHeading";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { useSeo } from "@/hooks/useSeo";
import { getPropertyCoverOffsetMap } from "@/lib/property-images";
import { normalizeListingSearchParams } from "@/lib/listing-search";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ViewMode = "grid" | "table";

export function PropertiesPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const normalized = normalizeListingSearchParams(params);

  if (normalized !== params) {
    return (
      <Navigate
        to={{ pathname: location.pathname, search: normalized.toString(), hash: location.hash }}
        replace
      />
    );
  }

  return <PropertyResults />;
}

function PropertyResults() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo<PropertyQuery>(() => {
    const get = (k: string) => searchParams.get(k) ?? undefined;
    const num = (k: string) => {
      const v = get(k);
      return v ? Number(v) : undefined;
    };
    return {
      listingType: get("listingType") as PropertyQuery["listingType"],
      propertyType: get("propertyType") as PropertyQuery["propertyType"],
      community: get("community"),
      minPrice: num("minPrice"),
      maxPrice: num("maxPrice"),
      minBedrooms: num("minBedrooms"),
      maxBedrooms: num("maxBedrooms"),
      minBathrooms: num("minBathrooms"),
      minArea: num("minArea"),
      maxArea: num("maxArea"),
      handoverFrom: get("handoverFrom"),
      handoverTo: get("handoverTo"),
      newDays: num("newDays"),
      query: get("q"),
      page: num("page") ?? 1,
      pageSize: num("pageSize") ?? 12,
      sort: (get("sort") as PropertyQuery["sort"]) ?? "newest",
      isFeatured: get("isFeatured") === "true" ? true : undefined,
      isExclusive: get("isExclusive") === "true" ? true : undefined,
    };
  }, [searchParams]);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; paramKeys: string[] }[] = [];
    if (query.community) chips.push({ key: "community", label: query.community, paramKeys: ["community"] });
    if (query.propertyType) {
      chips.push({
        key: "propertyType",
        label: query.propertyType.charAt(0).toUpperCase() + query.propertyType.slice(1),
        paramKeys: ["propertyType"],
      });
    }
    if (query.minBedrooms != null || query.maxBedrooms != null) {
      const min = query.minBedrooms;
      const max = query.maxBedrooms;
      const label =
        min != null && min === max
          ? min === 0
            ? "Studio"
            : `${min} ${min === 1 ? "bed" : "beds"}`
          : min != null
            ? `${min}+ beds`
            : `Up to ${max} beds`;
      chips.push({ key: "bedrooms", label, paramKeys: ["minBedrooms", "maxBedrooms"] });
    }
    if (query.minBathrooms != null) chips.push({ key: "minBathrooms", label: `${query.minBathrooms}+ baths`, paramKeys: ["minBathrooms"] });
    if (query.minPrice != null || query.maxPrice != null) {
      const fmt = (n?: number) => (n ? `${(n / 1_000_000).toFixed(n >= 1_000_000 ? 1 : 2).replace(/\.?0+$/, "")}M` : "");
      const min = query.minPrice ? fmt(query.minPrice) : "0";
      const max = query.maxPrice ? fmt(query.maxPrice) : "∞";
      chips.push({ key: "price", label: `AED ${min} – ${max}`, paramKeys: ["minPrice", "maxPrice"] });
    }
    if (query.minArea != null || query.maxArea != null) {
      const min = query.minArea ?? 0;
      const max = query.maxArea ?? "∞";
      chips.push({ key: "area", label: `${min} – ${max} sqft`, paramKeys: ["minArea", "maxArea"] });
    }
    if (query.handoverFrom || query.handoverTo) {
      const label =
        query.handoverFrom && query.handoverTo
          ? `Handover ${query.handoverFrom.slice(0, 7)} – ${query.handoverTo.slice(0, 7)}`
          : query.handoverFrom
            ? `Handover ≥ ${query.handoverFrom.slice(0, 7)}`
            : `Handover ≤ ${query.handoverTo!.slice(0, 7)}`;
      chips.push({ key: "handover", label, paramKeys: ["handoverFrom", "handoverTo"] });
    }
    if (query.newDays) {
      chips.push({ key: "newDays", label: `Newly listed (${query.newDays}d)`, paramKeys: ["newDays"] });
    }
    return chips;
  }, [query]);

  const [items, setItems] = useState<Property[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "grid";
    return (localStorage.getItem("hh-properties-view") as ViewMode) || "grid";
  });
  const { version } = useLiveProperties();
  const coverOffsets = useMemo(() => getPropertyCoverOffsetMap(items), [items]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("hh-properties-view", view);
    }
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listProperties(query)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, version]);

  const updateParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setSearchParams(next);
  };

  const heading = useMemo(() => {
    if (query.isFeatured) return "This Week's Picks.";
    if (query.isExclusive) return "Hive Exclusives.";
    switch (query.listingType) {
      case "rent":
        return "Homes to Rent.";
      case "sale":
        return "Homes for Sale.";
      default:
        return "Properties We Stand Behind.";
    }
  }, [query]);

  const subtitle = query.community
    ? `In ${query.community}, Dubai.`
    : "Dubai's most considered properties, in one place.";

  useSeo({
    title: "Dubai Properties for Sale & Rent | Hive Homes",
    description:
      "Browse 27+ hand-picked properties for sale and rent across Dubai. Apartments, villas, studios and townhouses in JVC, Business Bay, Tilal Al Ghaf and more. Hive Homes.",
  });

  return (
    <>
      {/* Hero — client banner photo behind the page heading */}
      <PageBanner
        image="/images/site/property.jpg"
        alt="Penthouse terrace overlooking the Dubai skyline at dusk"
      >
        <SectionHeading
          eyebrow="Our Listings"
          size="xl"
          title={heading}
          body={subtitle}
          as="h1"
        />
      </PageBanner>

      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]/60">
        <div className="container-wide py-8 md:py-10">
          <SearchBar defaultListingType={query.listingType === "rent" ? "rent" : "sale"} />
        </div>
      </section>

      <div className="container-wide py-8 md:py-14 short:py-8">
        {/* Listing-type tabs — quick filter without re-running search bar.
            Labels never wrap; on a very narrow phone the row scrolls
            sideways instead. The scroller overlaps the wrapper's rule by
            1px so the active underline sits on it. */}
        <div className="mb-6 border-b border-[var(--line)]">
          <div
            role="tablist"
            aria-label="Filter properties by listing type"
            className="-mb-px flex overflow-x-auto [scrollbar-width:none] sm:gap-2 [&::-webkit-scrollbar]:hidden"
          >
            {[
              { value: undefined, label: "All" },
              { value: "sale" as const, label: "For Sale" },
              { value: "rent" as const, label: "For Rent" },
            ].map((tab) => {
              const isActive = (query.listingType ?? undefined) === tab.value;
              return (
                <button
                  key={tab.label}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => updateParam("listingType", tab.value)}
                  className={cn(
                    "relative min-h-[44px] flex-1 whitespace-nowrap px-3 py-3 text-[11px] uppercase tracking-[0.14em] transition-colors sm:flex-initial sm:px-4 sm:text-[12.5px] sm:tracking-[0.18em]",
                    isActive
                      ? "text-[var(--text)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-soft)]",
                  )}
                >
                  {tab.label}
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-x-3 bottom-0 h-px bg-[var(--gold-bright)]"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Result count + controls; the controls drop to their own line
            (still right-aligned) when a narrow phone can't fit both. */}
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] pb-4 sm:pb-5">
          <p className="text-[14px] text-[var(--text-soft)]">
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-4 animate-spin text-[var(--cream)]" /> Hunting
              </span>
            ) : (
              <span className="inline-flex items-baseline gap-3">
                <span className="text-[var(--cream)] text-[15px]">{String(total).padStart(2, "0")}</span>
                <span className="meta uppercase tracking-[0.2em]">
                  {total === 1 ? "home found" : "homes found"}
                </span>
              </span>
            )}
          </p>
          <div className="ml-auto flex items-center gap-3 sm:gap-5">
            {/* View toggle */}
            <div
              role="tablist"
              aria-label="View mode"
              className="hidden sm:inline-flex items-center border border-[var(--line-strong)]"
            >
              <button
                type="button"
                role="tab"
                aria-selected={view === "grid"}
                aria-label="Grid view"
                onClick={() => setView("grid")}
                className={cn(
                  "flex size-11 items-center justify-center transition-colors lg:size-9",
                  view === "grid"
                    ? "bg-[var(--cream)] text-[var(--navy)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text)]",
                )}
              >
                <LayoutGrid className="size-4" />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "table"}
                aria-label="Table view"
                onClick={() => setView("table")}
                className={cn(
                  "flex size-11 items-center justify-center border-l border-[var(--line-strong)] transition-colors lg:size-9",
                  view === "table"
                    ? "bg-[var(--cream)] text-[var(--navy)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text)]",
                )}
              >
                <Rows3 className="size-4" />
              </button>
            </div>

            <span className="hidden sm:inline-block h-5 w-px bg-[var(--line-strong)]" />

            <div className="flex items-center gap-3">
              <span className="eyebrow text-[var(--text-muted)] hidden sm:inline">Sort by</span>
              <Select
                value={query.sort ?? "newest"}
                onValueChange={(v) => updateParam("sort", v)}
              >
                <SelectTrigger className="min-h-[44px] min-w-[160px] rounded-none border-0 border-b border-[var(--line-strong)] bg-transparent px-0 text-[14px] text-[var(--text)] shadow-none focus-visible:ring-0 sm:min-w-[180px] lg:min-h-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Just Listed</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="beds-desc">Beds: Most</SelectItem>
                  <SelectItem value="beds-asc">Beds: Fewest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {activeChips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  for (const k of c.paramKeys) next.delete(k);
                  next.delete("page");
                  setSearchParams(next);
                }}
                className="inline-flex min-h-[44px] items-center gap-1.5 border border-[var(--line-strong)] bg-[var(--surface)] px-3 py-1.5 text-[12px] text-[var(--text)] transition-colors hover:border-[var(--cream)] hover:text-[var(--cream)] lg:min-h-0"
              >
                {c.label}
                <X className="size-3" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                const keep = new URLSearchParams();
                if (query.listingType) keep.set("listingType", query.listingType);
                if (query.sort) keep.set("sort", query.sort);
                setSearchParams(keep);
              }}
              className="ml-1 inline-flex min-h-[44px] items-center px-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--cream)] lg:min-h-0 lg:px-0"
            >
              Clear all
            </button>
          </div>
        )}

        {error && (
          <p className="mt-8 border border-red-900/40 bg-red-950/30 p-6 text-sm text-red-300">
            {error}
          </p>
        )}

        {view === "table" ? (
          <div className="mt-8 sm:mt-10">
            {/* Table view — desktop+ only; falls back to grid on mobile */}
            <div className="hidden sm:block">
              <PropertyTable items={items} loading={loading} />
            </div>
            <div className="grid gap-x-6 gap-y-10 sm:hidden">
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-4">
                    <div className="aspect-[16/10] animate-pulse bg-[var(--surface-2)]" />
                    <div className="h-3 w-1/3 animate-pulse bg-[var(--surface-2)]" />
                    <div className="h-5 w-2/3 animate-pulse bg-[var(--surface-2)]" />
                  </div>
                ))}
              {!loading && items.map((p) => (
                <PropertyCard
                  key={p.id}
                  property={p}
                  coverOffset={coverOffsets.get(p.id) ?? 0}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8 grid gap-x-6 gap-y-10 sm:mt-10 sm:gap-x-8 sm:gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-4">
                  <div className="aspect-[16/10] animate-pulse bg-[var(--surface-2)]" />
                  <div className="h-3 w-1/3 animate-pulse bg-[var(--surface-2)]" />
                  <div className="h-5 w-2/3 animate-pulse bg-[var(--surface-2)]" />
                </div>
              ))}
            {!loading && items.map((p) => (
              <PropertyCard
                key={p.id}
                property={p}
                coverOffset={coverOffsets.get(p.id) ?? 0}
              />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <div className="mx-auto mt-16 max-w-md border-y border-[var(--line)] py-14 text-center">
            <Filter className="mx-auto mb-4 size-6 text-[var(--text-muted)]" />
            <p className="font-display text-[28px] text-[var(--text)]">
              Nothing matches, yet.
            </p>
            <p className="mt-3 text-[14px] text-[var(--text-soft)]">
              Try widening the price, beds or community, or drop us a line and
              we'll go hunting for you.
            </p>
            <button
              onClick={() => setSearchParams(new URLSearchParams())}
              className="btn btn-ghost mt-6"
            >
              Reset filters
            </button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-14 flex flex-col items-center gap-3 border-y border-[var(--line)] py-10 text-center sm:mt-16">
            <p className="meta uppercase tracking-[0.18em]">
              Can't find what you're looking for?
            </p>
            <p className="max-w-[44ch] text-[15px] leading-[1.6] text-[var(--text-soft)]">
              Tell us your brief and we'll search for you.
            </p>
            <Link to="/contact" className="link-arrow mt-2">
              Send Your Brief
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10m-4-4 4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            </Link>
          </div>
        )}

        <Pagination
          page={query.page ?? 1}
          pageSize={query.pageSize ?? 12}
          total={total}
          onChange={(p) => {
            const next = new URLSearchParams(searchParams);
            next.set("page", String(p));
            setSearchParams(next);
          }}
        />
      </div>
    </>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  return (
    <nav className="mt-12 flex flex-wrap items-center justify-center gap-1 sm:mt-16">
      <button
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="min-h-[44px] px-3 py-2 text-[12px] uppercase tracking-[0.22em] text-[var(--text-soft)] transition-colors hover:text-[var(--cream)] disabled:opacity-30 sm:px-4"
      >
        Back
      </button>
      {Array.from({ length: totalPages }).map((_, i) => {
        const n = i + 1;
        return (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={
              n === page
                ? "size-11 bg-[var(--cream)] text-[13px] text-[var(--navy)] lg:size-10"
                : "size-11 text-[14px] text-[var(--text-soft)] transition-colors hover:text-[var(--text)] lg:size-10"
            }
          >
            {n}
          </button>
        );
      })}
      <button
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className="min-h-[44px] px-3 py-2 text-[12px] uppercase tracking-[0.22em] text-[var(--text-soft)] transition-colors hover:text-[var(--cream)] disabled:opacity-30 sm:px-4"
      >
        Next
      </button>
    </nav>
  );
}
